import React from 'react';
import {Modal} from 'react-bootstrap';
import {FormattedMessage} from 'react-intl';
import classNames from 'classnames';
import debounce from 'lodash/debounce';

import {Client4} from 'mattermost-redux/client';
import {type RelationOneToOne} from 'mattermost-redux/types/utilities';
import {type Channel} from 'mattermost-redux/types/channels';
import {ClientError} from 'mattermost-redux/client/client4';
import {type UserProfile} from 'mattermost-redux/types/users';

import {TypographySize, typographyStyles} from '@time-webkit/all/hooks/typography';
import {type UserStatus} from '@time-webkit/all/types/general';
import {isGuest, isGuestPlus} from 'mattermost-redux/utils/user_utils';
import {displayEntireNameForUser, localizeAndFormatMessage, localizeMessage} from 'utils/utils';
import ProfilePicture from 'components/profile_picture';
import MultiSelect, {type Value} from 'components/multiselect/multiselect';
import AddIcon from 'components/widgets/icons/fa_add_icon';

import {GuestBadge, GuestPlusBadge, BotBadge} from '@time-webkit/all/atoms';

import ToggleModalButton from 'components/toggle_modal_button';

import Constants, {ModalIdentifiers, ServerErrorIds} from 'utils/constants';
import {SearchTimeMeasurer, sendToStatist} from '@time-webkit/statist';
import {InviteMembersModal} from '../invite_members_modal';
import {Alert, AlertType} from '@time-webkit/all/atoms/alert';
import {type UserAutocomplete} from '@mattermost/types/autocomplete';

import {type ChannelInviteModalActions} from '.';

import './channel_invite_modal.css';

const USERS_PER_PAGE = 50;

const searchTimeMeasurer = new SearchTimeMeasurer('channel.membersAdd.result');

type UserProfileValue = Value & UserProfile;

export type Props = {
    userStatuses: RelationOneToOne<UserProfile, UserStatus>;
    onExited: () => void;
    channel: Channel;

    // skipCommit = true used with onAddCallback will result in users not being committed immediately
    skipCommit?: boolean;

    // onAddCallback takes an array of UserProfiles and should set usersToAdd in state of parent component
    onAddCallback?: (userProfiles?: UserProfileValue[]) => void;

    // Dictionaries of userid mapped users to exclude or include from this list
    excludeUsers?: Record<string, UserProfileValue>;
    includeUsers?: Record<string, UserProfileValue>;

    isInvitationLinkVisible: boolean;
    actions: ChannelInviteModalActions;
}

type State = {
    values: UserProfileValue[];
    users: UserProfile[];
    term: string;
    show: boolean;
    saving: boolean;
    loadingUsers: boolean;
    inviteError?: string;
    guestsWithLimit: UserProfileValue[];
}

export default class ChannelInviteModal extends React.PureComponent<Props, State> {
    private selectedItemRef = React.createRef<HTMLDivElement>();

    public static defaultProps = {
        includeUsers: {},
        excludeUsers: {},
        skipCommit: false,
    };

    constructor(props: Props) {
        super(props);

        this.state = {
            values: [],
            users: [],
            term: '',
            show: true,
            saving: false,
            loadingUsers: true,
            guestsWithLimit: [],
        } as State;
    }

    private addValue = (value: UserProfileValue, number: number): void => {
        const values: UserProfileValue[] = Object.assign([], this.state.values);
        if (values.indexOf(value) === -1) {
            values.push(value);
        }

        sendToStatist('channel.membersAdd.resultTap', {
            resultNumber: number + 1,
            addType: 'direct',
            channelid: this.props.channel.id,
        });

        this.setState({values});
    };

    public componentDidMount(): void {
        this.props.actions.getProfilesNotInChannel(this.props.channel.team_id, this.props.channel.id, Boolean(this.props.channel.group_constrained), 0).then((data) => {
            if (!data.error && data.data) {
                this.setUsers(data.data);
            }
            this.setUsersLoadingState(false);
        });
        this.props.actions.getTeamStats(this.props.channel.team_id);
        this.props.actions.loadStatusesForProfilesList(this.state.users);
    }

    public onHide = (): void => {
        this.setState({show: false});
        this.props.actions.loadStatusesForProfilesList(this.state.users);
    };

    public handleInviteError = (err: any): void => {
        if (err) {
            this.setState({
                saving: false,
                inviteError: err.message,
            });
        }
    };

    private handleDelete = (values: UserProfileValue[]): void => {
        const guestsWithLimit = values.filter((value) => this.state.guestsWithLimit.includes(value));
        this.setState({
            values,
            guestsWithLimit,
        });
    };

    private setUsersLoadingState = (loadingState: boolean): void => {
        this.setState({
            loadingUsers: loadingState,
        });
    };

    private setUsers = (users: UserProfile[]): void => {
        this.setState({
            users,
        });
    };

    public handleSubmit = async (): Promise<void> => {
        const {actions, channel} = this.props;

        const userIds = this.state.values.map((v) => v.id);
        if (userIds.length === 0) {
            return;
        }

        const guestsToCheckPossibility = this.state.values.filter((value) => isGuest(value.roles));
        if (guestsToCheckPossibility.length > 0) {
            const guestsCheck = await Promise.all(
                guestsToCheckPossibility.map((guest) => {
                    return Client4.getUserAddChannelPossibility(guest.id)
                        .then(({status}) => (status ? null : guest))
                        .catch(() => guest);
                }),
            );

            const guestsWithLimit = guestsCheck.filter((guest): guest is UserProfileValue => Boolean(guest));

            if (guestsWithLimit.length > 0) {
                this.setState({guestsWithLimit});
                return;
            }
        }

        if (this.props.skipCommit && this.props.onAddCallback) {
            this.props.onAddCallback(this.state.values);
            this.setState({
                saving: false,
                inviteError: undefined,
            });
            this.onHide();
            return;
        }

        this.setState({saving: true});

        actions.addUsersToChannel(channel.id, userIds).then((result: any) => {
            if (result.error) {
                this.handleInviteError(result.error);
            } else {
                this.setState({
                    saving: false,
                    inviteError: undefined,
                });
                this.onHide();
            }
        }).catch((error: unknown) => {
            if (error instanceof ClientError && error.server_error_id === ServerErrorIds.INSUFFICIENT_PERMISSIONS) {
                this.props.actions.closeModal(ModalIdentifiers.CHANNEL_INVITE);
                this.props.actions.openInsufficientPermissionsModal('ADD_TO_CHANNEL');
            }
        });
    };

    private search = debounce(async (term: string) => {
        if (!term) {
            this.setUsersLoadingState(false);
            return;
        }

        searchTimeMeasurer.measure(term);

        const result = await this.props.actions.autocompleteUsers({
            term,
            teamId: this.props.channel.team_id,
            channelId: this.props.channel.id,
        });

        searchTimeMeasurer.measure(term, this.state.users.length, {channelid: this.props.channel.id});

        if (!result.error && result.data) {
            const data = result.data as UserAutocomplete;
            this.setUsers((data.out_of_channel ?? []).concat(data.users));
        }
        this.setUsersLoadingState(false);
    }, Constants.SEARCH_TIMEOUT_MILLISECONDS);

    public handleInput = (searchTerm: string): void => {
        const term = searchTerm.trim();

        this.setState({
            term,
        });
        this.setUsersLoadingState(true);

        this.search(term);
    };

    handleMembersInviteModalClose = () => {
        sendToStatist('channel.invite.teamInvite', {
            teamId: this.props.channel.team_id,
            channelId: this.props.channel.id,
        });
        this.props.actions.closeModal(ModalIdentifiers.CHANNEL_INVITE);
    };

    private renderAriaLabel = (option: UserProfileValue): string => {
        if (!option) {
            return '';
        }
        return option.username;
    };

    renderOption = (
        option: UserProfileValue,
        isSelected: boolean,
        onAdd: (user: UserProfileValue) => void,
        onMouseMove: (user: UserProfileValue) => void,
    ) => {
        let rowSelected = '';
        if (isSelected) {
            rowSelected = 'more-modal__row--selected';
        }

        return (
            <div
                key={option.id}
                ref={isSelected ? this.selectedItemRef : option.id}
                className={'more-modal__row clickable ' + rowSelected}
                onClick={() => onAdd(option)}
                onMouseMove={() => onMouseMove(option)}
            >
                <ProfilePicture
                    src={Client4.getProfilePictureUrl(option.id, option.last_picture_update)}
                    status={this.props.userStatuses[option.id]}
                    size={36}
                    userId={option.id}
                    username={option.username}
                />
                <div className='more-modal__details'>
                    <div className='more-modal__name'>
                        {displayEntireNameForUser(option)}
                        <BotBadge
                            show={Boolean(option.is_bot)}
                            className='badge-popoverlist'
                        />
                        <GuestPlusBadge
                            show={isGuestPlus(option.roles)}
                            className='popoverlist'
                        />
                        <GuestBadge
                            show={isGuest(option.roles)}
                            className='popoverlist'
                        />
                    </div>
                </div>
                <div className='more-modal__actions'>
                    <div className='more-modal__actions--round'>
                        <AddIcon />
                    </div>
                </div>
            </div>
        );
    };

    renderErrorAlert() {
        if (this.state.guestsWithLimit.length === 0) {
            return null;
        }

        return (
            <Alert
                className='limit-alert-invite'
                size='medium'
                type={AlertType.Danger}
                message={localizeMessage(
                    'channel_invite.limit_error.title',
                    'Failed to add',
                )}
                secondaryMessage={localizeAndFormatMessage(
                    'channel_invite.limit_error.desc',
                    'The channel limit for the guest has been reached at {members}',
                    {members: this.state.guestsWithLimit.map((user) => '@' + user.username).join(', ')},
                )}
            />
        );
    }

    renderInviteLink = () => {
        return (
            <ToggleModalButton
                className='btn btn-link'
                modalId={ModalIdentifiers.INVITE_MEMBERS}
                dialogType={InviteMembersModal}
                dialogProps={{initialChannels: [this.props.channel]}}
                onClick={this.handleMembersInviteModalClose}
            >
                <FormattedMessage id='channel_invite.invite_link' defaultMessage='Invite people to the team'/>
            </ToggleModalButton>
        );
    };

    renderCustomNoOptionsMessage = () => {
        return (
            <div
                className={classNames(
                    'custom-no-options-message',
                    typographyStyles[TypographySize.BodyL],
                    {'center-align': !this.props.isInvitationLinkVisible},
                )}
            >
                <FormattedMessage id='channel_invite.no_options_message' defaultMessage='No results found' />
                {this.props.isInvitationLinkVisible ? <>. {this.renderInviteLink()}</> : null}
            </div>
        );
    };

    render() {
        let inviteError = null;
        if (this.state.inviteError) {
            inviteError = (<label className='has-error control-label'>{this.state.inviteError}</label>);
        }

        const header = (
            <h1>
                <FormattedMessage
                    id='channel_invite.addNewMembers'
                    defaultMessage='Add people to {channel}'
                    values={{
                        channel: this.props.channel.display_name,
                    }}
                />
            </h1>
        );

        const buttonSubmitText = localizeMessage('multiselect.add', 'Add');
        const buttonSubmitLoadingText = localizeMessage('multiselect.adding', 'Adding...');

        let users = this.state.users as UserProfileValue[];

        if (this.props.includeUsers) {
            const includeUsers = Object.values(this.props.includeUsers);
            users = [...users, ...includeUsers];
        }

        const content = (
            <MultiSelect
                key='addUsersToChannelKey'
                options={users}
                optionRenderer={this.renderOption}
                selectedItemRef={this.selectedItemRef}
                values={this.state.values}
                ariaLabelRenderer={this.renderAriaLabel}
                saveButtonPosition={'bottom'}
                perPage={USERS_PER_PAGE}
                handleInput={this.handleInput}
                handleDelete={this.handleDelete}
                handleAdd={this.addValue}
                handleSubmit={this.handleSubmit}
                handleCancel={this.handleMembersInviteModalClose}
                buttonSubmitText={buttonSubmitText}
                buttonSubmitLoadingText={buttonSubmitLoadingText}
                saving={this.state.saving}
                loading={this.state.loadingUsers}
                placeholderText={localizeMessage('multiselect.placeholder', 'Search for people')}
                valueWithImage={true}
                backButtonText={localizeMessage('multiselect.cancel', 'Cancel')}
                backButtonClick={this.handleMembersInviteModalClose}
                backButtonClass={'btn-cancel tertiary-button'}
                customNoOptionsMessage={this.renderCustomNoOptionsMessage()}
            />
        );

        return (
            <Modal
                id='addUsersToChannelModal'
                dialogClassName='a11y__modal channel-invite'
                show={this.state.show}
                onHide={this.onHide}
                onExited={this.props.onExited}
                role='dialog'
                aria-labelledby='channelInviteModalLabel'
            >
                <Modal.Header
                    id='channelInviteModalLabel'
                    closeButton={true}
                />
                <Modal.Body
                    role='application'
                    className='overflow--visible'
                >
                    <div className='channel-invite__header'>
                        {header}
                    </div>
                    {inviteError}
                    <div className='channel-invite__content'>
                        {content}
                        {this.renderErrorAlert()}
                    </div>
                </Modal.Body>
            </Modal>
        );
    }
}
